import { FirebaseFunctionsError } from '../shared/customerrors';
import feFunctions, { functionsBaseUrl } from './fe-functions';

const REQUEST_SIZE_LIMIT_BYTES = 10 * 1024 * 1024; // 10 MB

// Form pages work without a builder login (completers use the template's stored
// connection or a signed link), so a dead builder session must not leave them.
export const FORM_ROUTE_STATES = ['render-template', 'render-dynamic'];

export default class FormEngineService {
  constructor($location, $window, $timeout, $state, firebase, AuthService, EventService) {
    'ngInject';
    this.devMode = !$location.host().includes('formengine.com');
    Object.assign(this, { $location, $window, $timeout, $state, firebase, AuthService, EventService });
    this.browsertFonts = [
      'arial', 'arial narrow', 'brush script mt', 'courier new', 'cursive', 
      'freestyle script', 'fontawesome', 'garamond', 'geneva', 'georgia', 'glyphicons halflings', 
      'helvetica', 'helvetica neue', 'inherit', 'menlo', 'monospace', 
      'pinyon script', 'sans-serif', 'serif', 
      'tahoma', 'times', 'times new roman', 'trebuchet ms', 'verdana'];
    this.buttonActions = {
      'submit': { customLabel: 'Submit', name: 'Submit' },
      'update': { customLabel: 'Update', name: 'Update' },
      'print': { customLabel: 'Print', name: 'Print' },
      'continue': { customLabel: 'Save & Keep Working', name: 'Continue' }
    };
    this.loadedGoogleFonts = new Set();
    this.loadingGoogleFonts = new Set();
  }

  get isDevMode() {
    return this.devMode;
  }

  async httpsCallable(name, request, retriedWithoutSession = false) {
    try {
      return await feFunctions(this.firebase).httpsCallable(name,  { timeout: 60*9*1000 })(request);
    } catch (err) {
      let errorObj = JSON.parse(JSON.stringify(err));
      let handled = false;
      errorObj.stack = err?.stack;
      errorObj.message = err.toString();
      
      if (errorObj?.code === 'unavailable') {
        errorObj.message = 'The service is temporarily unavailable. Please try again in a few moments.';
      } else if (errorObj?.code === 'invalid-argument') {
        const originalMessage = err?.message || errorObj?.message || '';
        if (!errorObj.details || typeof errorObj.details !== 'object') {
          errorObj.details = {};
        }
        if (!errorObj.details.message && originalMessage) {
          errorObj.details.message = originalMessage;
        }
        errorObj.message = (errorObj.details.message && !String(errorObj.details.message).startsWith('FirebaseError'))
          ? errorObj.details.message
          : 'Invalid request. Please check your input and try again.';
      } else if (err?.message?.toLowerCase().includes('cors') || errorObj?.message?.toLowerCase().includes('cors')) {
        errorObj.message = 'The browser blocked the request due to a security policy. Please contact support if this persists.';
        handled = true;
      }
      
      if (!handled && (errorObj?.code == 'permission-denied' || errorObj?.code == 'unauthenticated')) {
        console.error(`Authentication error: ${errorObj.code}!`);
        const errDetails = errorObj?.details || {};
        if (errDetails.logout && this.isFormRoute() && !retriedWithoutSession) {
          // A builder login left in this browser (pre-sessionId token, idle, or
          // ended session) is attached to every callable. On a form page, drop it
          // and retry as a completer instead of sending the user to login.
          await this.signOutDeadSession();
          return this.httpsCallable(name, request, true);
        }
        if (errDetails.logout) {
          await this.AuthService.logout(true, errorObj);
          this.$state.go('login', { provider: errDetails.provider, reload: true, validationErrorMessage: errDetails.validationErrorMessage });
          return;
        }
        if (errDetails.updateConnection) {
          // Invalid stored conn
          this.EventService.dispatchEvent(this.EventService.actions.AUTHENTICATE, {error: errorObj, updateConnection: true});
          return;
        }
        this.EventService.dispatchEvent(this.EventService.actions.AUTHENTICATE, {error: errorObj});
      }
      if (errorObj?.code == 'internal' && !handled) {
        errorObj.message += ` (${name}).`;
        errorObj.isOffline = await this.checkIfOffline(err);
        console.error(`Internal error: ${errorObj.details?.message || errorObj.toString()}!${errorObj.isOffline ? ' (User appears to be offline)' : ''}`);
        if (name !== 'frontendErrorLog') {
          if (errorObj.isOffline) {
            errorObj.message = 'You appear to be offline. Please check your internet connection and try again.';
          } else {
            const requestSizeBytes = this.getRequestSizeBytes(request);
            if (requestSizeBytes > REQUEST_SIZE_LIMIT_BYTES) {
              errorObj.message = 'Request is too large (over 10 MB). Please reduce the size of your submission—for example, use smaller or fewer attachments—and try again.';
            }
            errorObj.requestSize = this.getRequestSizeString(requestSizeBytes);
            await this.logFrontedError(name, errorObj, request);
          }
          this.EventService.dispatchEvent(this.EventService.actions.INTERNAL_ERROR, {error: errorObj});
        }
      }
      throw new FirebaseFunctionsError(errorObj);
    }
  }

  createRequestSkeleton(obj) {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) return [];
    if (typeof obj === 'object') {
      const skeleton = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          if (Array.isArray(value)) {
            skeleton[key] = [];
          } else if (value !== null && typeof value === 'object') {
            skeleton[key] = this.createRequestSkeleton(value);
          } else if (typeof value === 'string' && value.length >= 100) {
            skeleton[key] = '[PRUNED]';
          } else {
            skeleton[key] = value;
          }
        }
      }
      return skeleton;
    }
    return null;
  }

  getRequestSizeBytes(request) {
    try {
      return new TextEncoder().encode(JSON.stringify(request)).length;
    } catch {
      return 0;
    }
  }

  getRequestSizeString(requestSizeBytes) {
    const bytes = requestSizeBytes ?? 0;
    let size = bytes;
    let unit = 'B';

    if (bytes >= 1024 * 1024) {
      size = (bytes / (1024 * 1024)).toFixed(2);
      unit = 'MB';
    } else if (bytes >= 1024) {
      size = (bytes / 1024).toFixed(2);
      unit = 'KB';
    }

    return `${size} ${unit}`;
  }

  async logFrontedError(name, errorObj, request) {
    try {
      const requestSkeleton = this.createRequestSkeleton(request);
      const logRequest = {
        ...requestSkeleton,
        caller: name,
        errorObj
      };
      await this.httpsCallable('frontendErrorLog', logRequest);
    } catch (logError) {
      console.error('Failed to publish error log:', logError);
    }
  }

  getRequestFuncsUrl() {
    const {emulatorOrigin, region, app} = feFunctions(this.firebase);
    return this.isLocal() ?
      `${emulatorOrigin}/${app.options.projectId}/${region}/` :
      `${functionsBaseUrl()}/`;
  }
  
  //expand to accept arrays of new userMessages from firebase
  updateMessages(userMessages, rawMessage){
    
    let { message, alertClass, error, when } = rawMessage;
    
    if(error) {
      const errMsg = String(error.message || error).replace(/\.+$/, ''); // no double periods
      message = `An Error occurred ${when}.\n Message: ${errMsg}.`;
      alertClass = alertClass || 'danger';
    } 

    if (userMessages.map(um => um.message).includes(message)) {
      return userMessages;
    } 
    
    //TODO should move away from having 'class' as a key (my bad)
    return [...userMessages, { message, class: `alert-${alertClass}`}];
  }
  
  watchForBinding(binding, scope, callback) {
    return new Promise((res) => {
      const stopWatching = scope.$watch(`$ctrl.${binding}`, (newVal) => {
        if (newVal !== undefined) { //TODO prevent callback if newVal === oldVal?
          callback ? callback(newVal) : stopWatching();
          res(newVal);
        }
      });
    });
  }
  
  waitForBindings(bindings, scope) {
    const promises = bindings.map( b => this.watchForBinding(b, scope) );
    return Promise.all(promises);
  }
  
  isLocal() {
    return this.$location.host().includes('localhost') || this.$location.host().includes('127.0.0.1');
  }

  async checkIfOffline(_originalError) {
    const lightRequestEndpoints = ['https://www.google.com/favicon.ico', 'https://www.cloudflare.com/favicon.ico'];
    const pingPromises = lightRequestEndpoints.map(url => 
      fetch(url, { method: 'HEAD', cache: 'no-cache', mode: 'no-cors', signal: AbortSignal.timeout(3000)})
        .then(() => true).catch(() => false));
    try {
      const firstResult = await Promise.race(pingPromises);
      if (firstResult === true) return false;
      const results = await Promise.allSettled(pingPromises);
      const anySucceeded = results.some(result => result.status === 'fulfilled' && result.value === true );
      return !anySucceeded;
    } catch {
      return true;
    }
  }
  
  builderUrl(template) {
    const route = template.type === 'dynamic' ? 'dynamic-builder' : 'template-builder';
    return `/#!/${route}/${template.id}`;
  }

  getRenderErrorAction(error, successType, previousAction) {
    const msg = error.message;
    
    if (successType === 'submit' && previousAction === 'submit') {
      return 'writeOnlySubmit';
    } else if (msg.includes('Sign-in required') || msg.includes('Bad ticket')) {
      return 'promptLogin';
    } else {
      return null;
    }
  }

  getFieldId(fieldId) {
    return fieldId.startsWith('form_engine_attachment') ? 'form_engine_attachment': fieldId;
  }
  
  handleRenderErrorMessage(userMessage, resetNoAccess, setCriticalError) {
    const msg = userMessage.message;
    
    if (msg.startsWith('You cannot change the value of this field: ')) {
      userMessage.message += `. Please try again, or contact the form creator.`;
      let [namePart, fid] = msg.split('" with field id ');
      let label = namePart.split('restricted field named "')[1];
      resetNoAccess(fid, label);
      
    } else if (msg.match(/^Custom data rule failed: /g)) {
      //handle 1+ occurnces of cdrf in message (why, QB?)
      const cdrf = "Custom data rule failed: ";
      const messageParts = msg.split(cdrf);
      const revisedMsg = cdrf + messageParts[messageParts.length-1];
      userMessage.message = revisedMsg;
      
    } else if (!msg.startsWith('Missing required data!')) {
      if (msg.startsWith("Error: internal")) userMessage.message=`Service unavailable, please try again. ${msg}`;
      setCriticalError();
    }
  }

  // Only http(s) URLs are valid. javascript:, data:, etc. run script when assigned to location.
  isValidUrl(url) {
    try {
      const { protocol } = new URL(url);
      return protocol === 'http:' || protocol === 'https:';
    }
    catch { 
      return false; 
    }
  }

  safeParseJSON(jsonString) {
    try {
      const json = JSON.parse(jsonString);
      return typeof json === 'object' && json != null ? json : {};
    } 
    catch { return {} }
  }

  isFormRoute() {
    return FORM_ROUTE_STATES.includes(this.$state?.current?.name || '');
  }

  // The server already rejected this session, so there is no live row to revoke
  // (an idle row is pruned on the next login). A local sign-out is enough, and
  // avoids AuthService.logout's "Could not log out" flash on a form page.
  async signOutDeadSession() {
    await this.firebase.auth().signOut();
  }

  isBuilderRoute() {
    const name = this.$state?.current?.name || '';
    return name === 'app.form-builder' || name === 'app.template-builder' || name === 'app.dynamic-builder';
  }

  normalizeGoogleFontName(fontFragment) {
    const fontName = fontFragment.replace(/["']|!important/g, '').trim();
    if (!fontName || !/[A-Z]/.test(fontName)) return null;
    if (this.browsertFonts.includes(fontName.toLowerCase())) return null;
    return fontName;
  }

  addGoogleFontsFromFamilyList(familyList, allFonts) {
    familyList.split(',').forEach((fragment) => {
      const fontName = this.normalizeGoogleFontName(fragment);
      if (fontName) allFonts.add(fontName);
    });
  }

  collectGoogleFontsFromComputedStyles(selector, allFonts) {
    $(selector).toArray().forEach((el) => {
      const { fontFamily } = getComputedStyle(el);
      if (fontFamily) this.addGoogleFontsFromFamilyList(fontFamily, allFonts);
    });
  }

  collectGoogleFontsFromCssText(cssText, allFonts) {
    if (!cssText) return;
    const regex = /font-family\s*:\s*([^;{}]+)/gi;
    let match;
    while ((match = regex.exec(cssText)) !== null) {
      this.addGoogleFontsFromFamilyList(match[1], allFonts);
    }
  }

  collectGoogleFontsFromStyleElements(styleSelector, allFonts) {
    if (!styleSelector) return;
    $(styleSelector).toArray().forEach((styleEl) => {
      this.collectGoogleFontsFromCssText(styleEl.textContent, allFonts);
    });
  }

  getFontScanTargets(isDynamic, rendering) {
    if (!isDynamic) {
      return {
        computedSelectors: '.token-container, fe-control.token-container',
        styleSelectors: rendering ? '.render-template style, #render-container style' : null,
      };
    }
    if (rendering) {
      return {
        computedSelectors: 'fe-control, #render-content [style*="font-family"]',
        styleSelectors: '#render-content style',
      };
    }
    return {
      computedSelectors: 'span.fe-token:not(.cke_widget_element), .cke_editable [style*="font-family"], #dynamicEditor [style*="font-family"]',
      styleSelectors: '.cke_editable style, #dynamicEditor style',
    };
  }

  collectGoogleFonts(isDynamic, rendering, extraCss, allFonts) {
    const { computedSelectors, styleSelectors } = this.getFontScanTargets(isDynamic, rendering);
    this.collectGoogleFontsFromComputedStyles(computedSelectors, allFonts);
    this.collectGoogleFontsFromStyleElements(styleSelectors, allFonts);
    this.collectGoogleFontsFromCssText(extraCss, allFonts);
  }

  getFontsToLoad(fontNames) {
    return fontNames.filter((fontName) =>
      !this.loadedGoogleFonts.has(fontName)
      && !this.loadingGoogleFonts.has(fontName)
    );
  }

  clearInFlightGoogleFonts(fontNames) {
    fontNames.forEach((fontName) => this.loadingGoogleFonts.delete(fontName));
    if (this.loadingGoogleFonts.size === 0) {
      this.loadingFonts = false;
    }
  }

  async loadMissingFonts(isDynamic, rendering, extraCss = '') {

    let error = false;
    if (!this.$window.WebFont) {
      console.error("Error loading google web fonts API!");
      return true;
    }

    const allFonts = new Set();
    this.collectGoogleFonts(isDynamic, rendering, extraCss, allFonts);

    const fontArray = this.getFontsToLoad(Array.from(allFonts));
    if (fontArray.length === 0) return false;

    fontArray.forEach((fontName) => this.loadingGoogleFonts.add(fontName));

    const onFontRequestSettled = () => {
      if (this.loadingGoogleFonts.size === 0) {
        this.loadingFonts = false;
      }
    };

    this.loadingFonts = true;
    this.$window.WebFont.load({ 
      google: { families: fontArray },
      fontactive: (familyName) => {
        this.loadedGoogleFonts.add(familyName);
        this.loadingGoogleFonts.delete(familyName);
        onFontRequestSettled();
      },
      fontinactive: (familyName, _fvd) => {
        error = true;
        this.loadingGoogleFonts.delete(familyName);
        console.log(`Error loading font "${familyName}"!`);
        onFontRequestSettled();
      },
      active: onFontRequestSettled,
      inactive: onFontRequestSettled,
    });
    console.info(`Loading google fonts: ${fontArray.join(', ')}`);

    return new Promise((resolve) => {
      const interval = 100;
      let elapsedTime = 0;
      const checkFontsLoaded = _ => {
        if (!this.loadingFonts) {
          console.log("Google fonts loaded!");
          resolve(error);
          return;
        }
        // Don't wait more than 10 secs.
        if (elapsedTime >= 10000) {
          console.log("Couldn't load google fonts!");
          this.clearInFlightGoogleFonts(fontArray);
          resolve(true);
          return;
        }
        elapsedTime += interval;
        this.$timeout(checkFontsLoaded, interval);
      };
      checkFontsLoaded();
    });
  }
}
